import React, { useEffect, useMemo, useState } from 'react';
import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  captureVehicleWeight,
  getWeighBridgeWorkCenters,
} from 'src/redux/Actions/VehicleTracking';
import {
  getCapturedWeightForWeightBridgeEventUuid,
  getDataForVehicleTrackingLoadCards,
  getVehicleTrackingAttachedLoads,
  updateVehicleTrackingLoadWeight,
} from 'src/redux/Actions/VehicleTrackingOutward';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as VehicleWeightIcon } from 'src/Images/icons/common/VehicleWeightIcon.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as TargetInfoIcon } from 'src/Images/icons/common/target-info-icon.svg';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';

const LOAD_STATUS = {
  TARE_WEIGHT: 1,
  GROSS_WEIGHT: 3,
};

export default function VehicleTrackingManageTareWeight({
  onBack,
  themeColor,
  vtLoadHeaderUniqueNumber,
}) {
  const { workflowStageId, vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const canCaptureLoadTareWeight = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CAPTURE_LOAD_WEIGHT_1,
  );
  const canConfirmLoadWeight = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CONFIRM_LOAD_WEIGHT,
  );

  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const { vehicleTrackingLoadDetails, vehicleTrackingAttachedLoads } =
    VehicleTrackingOutwardReducer;
  const { weighBridgeWorkCenters, vehicleTrackingDetails } =
    vehicleTrackingDataReducer;

  const [selectedWeightBridge, setSelectedWeightBridge] = useState(null);
  const [captureWeight, setCaptureWeight] = useState(false);
  const [weightBridgeEventUUID, setWeightBridgeEventUUID] = useState(null);
  const [captureWeightLoading, setCaptureWeightLoading] = useState(false);
  const [capturedTareWeight, setCapturedTareWeight] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [nextWeightToPrefill, setNextWeightToPrefill] = useState(null);

  // Derived from status?.id: 1 → 'Tare Weight', 3 → 'Gross Weight'
  const cardFieldName =
    vehicleTrackingLoadDetails?.status?.id === LOAD_STATUS.GROSS_WEIGHT
      ? 'Gross Weight'
      : 'Tare Weight';

  const fieldVerboseName = cardFieldName;
  const weightBridgeOptions = useMemo(
    () =>
      weighBridgeWorkCenters?.map((option) => ({
        value: option.id,
        label: option.name,
        ...option,
      })) || [],
    [weighBridgeWorkCenters],
  );

  useEffect(() => {
    dispatch(getWeighBridgeWorkCenters(tenantOrganisationId, '1'));
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (!selectedWeightBridge && weightBridgeOptions.length > 0) {
      setSelectedWeightBridge(weightBridgeOptions[0]);
    }
  }, [selectedWeightBridge, weightBridgeOptions]);

  useEffect(() => {
    if (
      !tenantOrganisationId ||
      !vehicleTrackingNumber ||
      !vtLoadHeaderUniqueNumber
    )
      return;

    dispatch(
      getDataForVehicleTrackingLoadCards(
        tenantOrganisationId,
        vehicleTrackingNumber,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }, [tenantOrganisationId, vehicleTrackingNumber, vtLoadHeaderUniqueNumber]);

  useEffect(() => {
    const loadList = vehicleTrackingAttachedLoads?.vehicleTrackingLoadData;
    const isFirstLoadTransaction = loadList?.every(
      (loadObj) => loadObj?.load_sequence === null,
    );

    // Case 1: Initial case, i.e., no load has been managed yet
    if (
      vehicleTrackingAttachedLoads?.isLoadSequenceEnabled === false &&
      isFirstLoadTransaction
    ) {
      setNextWeightToPrefill(vehicleTrackingDetails?.tareWeight ?? null);
      return;
    }

    // Case 2: A managed load already exists. Find the highest load_sequence
    // and use its actual_load_gross_weight for prefill
    if (
      vehicleTrackingAttachedLoads?.isLoadSequenceEnabled === false &&
      !isFirstLoadTransaction
    ) {
      const highestSequenceLoad = loadList
        ?.filter((load) => load.load_sequence !== null)
        .reduce((maxLoad, currentLoad) =>
          currentLoad.load_sequence > maxLoad.load_sequence
            ? currentLoad
            : maxLoad,
        );
      setNextWeightToPrefill(highestSequenceLoad?.actual_gross_weight ?? null);
    }
  }, [vehicleTrackingAttachedLoads, vehicleTrackingDetails]);

  useEffect(() => {
    if (!vehicleTrackingLoadDetails) return;

    if (
      nextWeightToPrefill !== null &&
      vehicleTrackingLoadDetails?.status?.id === LOAD_STATUS.TARE_WEIGHT
    ) {
      setCapturedTareWeight(nextWeightToPrefill);
      return;
    }

    setCapturedTareWeight(vehicleTrackingLoadDetails?.actualTareWeight ?? null);
  }, [vehicleTrackingLoadDetails, nextWeightToPrefill]);

  useEffect(() => {
    if (!captureWeight) return undefined;

    let attemptCounter = 0;

    const checkWeightBridgeData = () => {
      dispatch(
        getCapturedWeightForWeightBridgeEventUuid(
          tenantOrganisationId,
          vtLoadHeaderUniqueNumber,
          weightBridgeEventUUID,
          vehicleTrackingNumber,
          fieldVerboseName,
        ),
      ).then((data) => {
        const hasNullActualValue = data?.actualValue === null;

        if (!hasNullActualValue) {
          setCapturedTareWeight(data?.actualValue);
        }

        if (hasNullActualValue && attemptCounter < 5) {
          attemptCounter += 1;
          setTimeout(checkWeightBridgeData, 5000);
        } else {
          setCaptureWeight(false);
          setCaptureWeightLoading(false);
        }
      });
    };

    setCaptureWeightLoading(true);
    checkWeightBridgeData();

    return () => setCaptureWeight(false);
  }, [captureWeight]);

  function handleCaptureWeight() {
    if (!canCaptureLoadTareWeight || !selectedWeightBridge) return;

    const uuid = crypto.randomUUID();
    setWeightBridgeEventUUID(uuid);
    setCaptureWeight(true);

    dispatch(
      captureVehicleWeight(
        tenantOrganisationId,
        vehicleTrackingNumber,
        fieldVerboseName,
        'vehicletrackingloadheader',
        workflowStageId,
        selectedWeightBridge?.id,
        uuid,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }

  function handleConfirmWeight() {
    if (!canConfirmLoadWeight) return;

    const weightValue =
      capturedTareWeight ?? vehicleTrackingLoadDetails?.actualTareWeight;
    if (weightValue == null) return;

    setIsConfirming(true);

    const updateLoadWeightPayload = {
      weight_value: weightValue,
      vt_load_header_unique_number: vtLoadHeaderUniqueNumber,
      vehicle_tracking_number: vehicleTrackingNumber,
      field_verbose_name: cardFieldName,
    };

    dispatch(updateVehicleTrackingLoadWeight(updateLoadWeightPayload))
      .then(() => {
        dispatch(
          getDataForVehicleTrackingLoadCards(
            tenantOrganisationId,
            vehicleTrackingNumber,
            vtLoadHeaderUniqueNumber,
          ),
        );
        dispatch(
          getVehicleTrackingAttachedLoads(
            tenantOrganisationId,
            vehicleTrackingNumber,
          ),
        );
        onBack();
      })
      .finally(() => {
        setIsConfirming(false);
      });
  }

  useEffect(
    () => () => {
      setNextWeightToPrefill(null);
    },
    [],
  );

  const statusName = vehicleTrackingLoadDetails?.status?.name || 'Tare Weight';
  const loadCode = vehicleTrackingLoadDetails?.loadCode || '--';
  const displayWeight =
    capturedTareWeight ?? vehicleTrackingLoadDetails?.actualTareWeight;
  const weightUom = vehicleTrackingLoadDetails?.actualTareWeightUom || 'KG';
  const confirmWeightEnabled = displayWeight != null && !isConfirming;

  return (
    <div className="div-flex-column rg-10 h-100">
      {/* Header */}
      <div className="div-flex-row div-space-between div-align-center padding-top-10">
        <div className="div-flex-row cg-20 div-align-center">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-bold">{statusName}</span>
          </div>
          <span className="light-blue-pill text-md">{loadCode}</span>
        </div>
        <button
          className={`vehicle-data-form-submit-btn theme-background-${themeColor}`}
          onClick={handleConfirmWeight}
          disabled={!confirmWeightEnabled}
        >
          <span className="text-md">
            {isConfirming ? 'Confirming...' : 'Confirm Weight'}
          </span>
        </button>
      </div>

      {/* Weight gauge */}
      <div className="weight-icon-container">
        <div
          className={`vehicle-weight-gauge ${
            displayWeight == null ? 'no-progress' : 'has-progress'
          }`}
        >
          <VehicleWeightIcon className="vehicle-weight-svg" />
          <div className="vehicle-weight-content">
            <div className="text-xl text-semi-bold w-100 text-nowrap">
              {statusName}
            </div>

            <div className="vehicle-weight-value text-xl text-semi-bold">
              {captureWeightLoading ? (
                <div className="capture-weight-animation">
                  <span className="capture-weight-loading-dot" />
                  <span className="capture-weight-loading-dot" />
                  <span className="capture-weight-loading-dot" />
                </div>
              ) : (
                <div className="div-flex-row div-align-center cg-5">
                  <span>{displayWeight ?? '0'}</span>
                  {displayWeight != null && <span>{weightUom}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info banner */}
      {canCaptureLoadTareWeight && (
        <>
          {/* Info banner */}
          <div className="bg-light-grey text-md div-flex-row cg-5 div-align-center p-1">
            <TargetInfoIcon className="icon-size-sm" />
            To update the tare weight select the weight bridge below &amp;
            capture weight again.
          </div>

          {/* Select Weight Bridge + Capture */}
          <div className="div-flex-row div-align-center div-space-between">
            <Col xxl={4} xl={4} lg={4} md={4} className="form-input-container">
              <div className="div-flex-column">
                <div className="div-flex-row">
                  <span className="text-sm text-bold">
                    Select Weight Bridge
                  </span>
                </div>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    options={weightBridgeOptions}
                    value={selectedWeightBridge}
                    onChange={(selectedOption) =>
                      setSelectedWeightBridge(selectedOption)
                    }
                    placeholder="Select a weight bridge..."
                    isSearchable
                    className="multi-select-input-section"
                    classNamePrefix="react-select"
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </div>
            </Col>

            <button
              className={`vehicle-data-form-submit-btn theme-background-${themeColor}`}
              onClick={handleCaptureWeight}
              disabled={!selectedWeightBridge || captureWeightLoading}
            >
              <span className="text-md">
                {captureWeightLoading ? 'Capturing...' : 'Capture Weight'}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
